// La lista de mensajes de un chat abierto, compartida por el chat 1 a 1
// (ChatConversation) y el de sala (EventRoomContentElementChatChannel).
//
// Los mensajes llegan por varios caminos: la carga inicial, MessageSent (la
// cola puede entregarlo dos veces y no garantiza el orden), la respuesta al
// enviar uno propio y la recarga al reconectarse. Todo pasa por acá para que
// no haya duplicados y el orden sea siempre el de los ids.
//
// Un mensaje propio sin `id` todavía no llegó al backend: lleva `status`
// 'sending' o 'failed' (con `failure`: 'rate_limited' o 'error') y va al final.

const sentById = (a, b) => parseInt(a.id) - parseInt(b.id)

export const sortMessages = (messages) => [
  ...messages.filter((row) => row.id).sort(sentById),
  ...messages.filter((row) => !row.id),
]

const sameMessage = (a, b) =>
  (a.id && b.id && a.id === b.id) || (a.uuid && b.uuid && a.uuid === b.uuid)

// Agrega o reemplaza un mensaje (por id o uuid) y deja la lista ordenada.
export const upsertMessage = (messages, message) => {
  const exists = messages.some((row) => sameMessage(row, message))
  return sortMessages(
    exists
      ? messages.map((row) => (sameMessage(row, message) ? message : row))
      : [...messages, message],
  )
}

// El mensaje propio que se estaba enviando (uuid local) pasa a ser el que
// guardó el backend, que trae su propio uuid y el id.
export const confirmMessage = (messages, localUuid, saved) =>
  upsertMessage(
    messages.filter((row) => row.uuid !== localUuid),
    saved,
  )

export const updateLocal = (messages, localUuid, changes) =>
  messages.map((row) => (row.uuid === localUuid ? { ...row, ...changes } : row))

// Una página entera nueva (la última) en lugar de lo que había, más lo propio
// que todavía no llegó al backend.
export const mergeFetched = (messages, fetched) =>
  sortMessages([...fetched, ...messages.filter((row) => !row.id)])

// Mensajes sueltos (los posteriores al reconectarse, o una página anterior)
// sumados a lo que ya había, sin duplicar.
export const mergeMessages = (messages, fetched) => {
  const known = new Set(
    messages.filter((row) => row.id).map((row) => String(row.id)),
  )
  return sortMessages([
    ...messages,
    ...fetched.filter((row) => !known.has(String(row.id))),
  ])
}

const sentIds = (messages) =>
  messages.filter((row) => row.id).map((row) => parseInt(row.id))

export const newestId = (messages) => {
  const ids = sentIds(messages)
  return ids.length ? Math.max(...ids) : null
}

export const oldestId = (messages) => {
  const ids = sentIds(messages)
  return ids.length ? Math.min(...ids) : null
}

// Cerca del borde de arriba: es hora de pedir la página anterior.
export const LOAD_OLDER_THRESHOLD_PX = 80

export const sendFailure = (err) =>
  err && err.response && err.response.status === 429 ? 'rate_limited' : 'error'

// Espera al azar antes de recargar al reconectarse: si se cae la conexión de
// todo un venue, no vuelven todos al backend en el mismo segundo.
export const RECONNECT_JITTER_MS = 3000
